import React, { useState } from 'react';
import { Mail, Lock, Eye, EyeOff, Phone, Smartphone, ChevronLeft, ShoppingBag, Store } from 'lucide-react';
import { motion } from 'framer-motion';
import { useNavigation } from '../context/NavigationContext';
import { useApp } from '../context/AppContext';
import { Screen } from '../types';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
export function LoginScreen() {
  const {
    navigate,
    resetTo,
    goBack
  } = useNavigation();
  const {
    setUser,
    toggleVendorMode,
    isVendorMode
  } = useApp();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loginMethod, setLoginMethod] = useState<'email' | 'phone'>('email');
  const [accountType, setAccountType] = useState<'customer' | 'vendor'>('customer');
  const handleLogin = () => {
    setLoading(true);
    setTimeout(() => {
      setUser({
        id: 'u1',
        name: 'John Doe',
        email: 'john@example.com',
        isVendor: accountType === 'vendor'
      });
      if (accountType === 'vendor' && !isVendorMode) {
        toggleVendorMode();
      } else if (accountType === 'customer' && isVendorMode) {
        toggleVendorMode();
      }
      setLoading(false);
      resetTo(accountType === 'vendor' ? Screen.VENDOR_DASHBOARD : Screen.HOME);
    }, 1000);
  };
  const handlePhoneLogin = () => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      navigate(accountType === 'vendor' ? Screen.VENDOR_VERIFY : Screen.OTP);
    }, 1000);
  };
  const handleSocialLogin = (provider: string) => {
    setLoading(true);
    setTimeout(() => {
      setUser({
        id: 'u1',
        name: provider === 'Google' ? 'John Doe' : provider === 'Apple' ? 'John D.' : 'John Doe',
        email: 'john@example.com',
        isVendor: accountType === 'vendor'
      });
      if (accountType === 'vendor' && !isVendorMode) {
        toggleVendorMode();
      } else if (accountType === 'customer' && isVendorMode) {
        toggleVendorMode();
      }
      setLoading(false);
      resetTo(accountType === 'vendor' ? Screen.VENDOR_DASHBOARD : Screen.HOME);
    }, 1200);
  };
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} className="min-h-screen bg-gray-50 flex flex-col" data-id="element-767">
      {/* Branded Header */}
      <div className="bg-gradient-to-br from-primary to-primary-800 pt-12 pb-16 px-6 relative" data-id="element-768">
        <button onClick={goBack} className="absolute top-12 left-4 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors" data-id="element-769">
          <ChevronLeft className="w-6 h-6" data-id="element-770" />
        </button>

        <div className="flex flex-col items-center text-center mt-4" data-id="element-771">
          <img src="https://cdn.magicpatterns.com/uploads/qwjBm9dydTfJXkZFWZ9RtX/JUMMALL_UPDATED_LOGO_(1).png" alt="JUMMALL Logo" className="w-32 h-auto object-contain mb-6 drop-shadow-md" data-id="element-772" />
          <h1 className="text-2xl font-bold text-white mb-1" data-id="element-773">Welcome Back!</h1>
          <p className="text-primary-100 text-sm" data-id="element-774">
            {accountType === 'vendor' ? 'Login to manage your store' : 'Login to continue shopping'}
          </p>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 bg-white rounded-t-[32px] -mt-6 px-6 pt-8 pb-6 flex flex-col shadow-[0_-8px_30px_rgba(0,0,0,0.1)]" data-id="element-775">
        {/* Account Type Selector */}
        <div className="flex bg-gray-100 rounded-xl p-1 mb-5" data-id="element-776">
          <button onClick={() => setAccountType('customer')} className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg text-sm font-semibold transition-all duration-200 ${accountType === 'customer' ? 'bg-white text-primary shadow-sm' : 'text-gray-500'}`} data-id="element-777">
            <ShoppingBag className="w-4 h-4" data-id="element-778" />
            Customer
          </button>
          <button onClick={() => setAccountType('vendor')} className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg text-sm font-semibold transition-all duration-200 ${accountType === 'vendor' ? 'bg-white text-accent shadow-sm' : 'text-gray-500'}`} data-id="element-779">
            <Store className="w-4 h-4" data-id="element-780" />
            Vendor
          </button>
        </div>

        {/* Email / Phone Toggle */}
        <div className="flex bg-gray-100 rounded-xl p-1 mb-6" data-id="element-781">
          <button onClick={() => setLoginMethod('email')} className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${loginMethod === 'email' ? 'bg-white text-primary shadow-sm' : 'text-gray-500'}`} data-id="element-782">
            <Mail className="w-4 h-4" data-id="element-783" />
            Email
          </button>
          <button onClick={() => setLoginMethod('phone')} className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${loginMethod === 'phone' ? 'bg-white text-primary shadow-sm' : 'text-gray-500'}`} data-id="element-784">
            <Smartphone className="w-4 h-4" data-id="element-785" />
            Phone
          </button>
        </div>

        {/* Login Form */}
        {loginMethod === 'email' ? <motion.div key="email" initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.2
      }} className="flex flex-col gap-4 mb-6" data-id="element-786">
            <Input label="Email Address" type="email" placeholder="Enter your email" icon={<Mail className="w-5 h-5" data-id="element-788" />} data-id="element-787" />
            <div data-id="element-789">
              <Input label="Password" type={showPassword ? 'text' : 'password'} placeholder="Enter your password" icon={<Lock className="w-5 h-5" data-id="element-791" />} rightIcon={<button type="button" onClick={() => setShowPassword(!showPassword)} data-id="element-792">
                    {showPassword ? <EyeOff className="w-5 h-5" data-id="element-793" /> : <Eye className="w-5 h-5" data-id="element-794" />}
                  </button>} data-id="element-790" />
              <div className="flex justify-end mt-2" data-id="element-795">
                <button onClick={() => navigate(Screen.FORGOT_PASSWORD)} className="text-sm font-medium text-primary hover:underline" data-id="element-796">
                  Forgot Password?
                </button>
              </div>
            </div>
          </motion.div> : <motion.div key="phone" initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.2
      }} className="flex flex-col gap-4 mb-6" data-id="element-797">
            <Input label="Phone Number" type="tel" placeholder="+234 800 000 0000" icon={<Phone className="w-5 h-5" data-id="element-799" />} data-id="element-798" />
            <p className="text-xs text-gray-500 -mt-2" data-id="element-800">
              We'll send you a one-time verification code via SMS
            </p>
          </motion.div>}

        <Button fullWidth size="lg" onClick={loginMethod === 'email' ? handleLogin : handlePhoneLogin} loading={loading} className="shadow-md" data-id="element-801">
          {loginMethod === 'email' ? accountType === 'vendor' ? 'Login as Vendor' : 'Login' : 'Send OTP'}
        </Button>

        {/* Divider */}
        <div className="my-6 flex items-center gap-4" data-id="element-802">
          <div className="flex-1 h-px bg-gray-200" data-id="element-803"></div>
          <span className="text-sm text-gray-400 font-medium" data-id="element-804">
            or continue with
          </span>
          <div className="flex-1 h-px bg-gray-200" data-id="element-805"></div>
        </div>

        {/* Social Login Buttons */}
        <div className="flex flex-col gap-3 mb-auto" data-id="element-806">
          <button onClick={() => handleSocialLogin('Google')} className="w-full flex items-center justify-center gap-3 py-3 px-4 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 active:scale-[0.98] transition-all" data-id="element-807">
            <svg className="w-5 h-5" viewBox="0 0 24 24" data-id="element-808">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" data-id="element-809" />
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" data-id="element-810" />
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" data-id="element-811" />
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" data-id="element-812" />
            </svg>
            <span className="text-sm font-semibold text-gray-700" data-id="element-813">
              Continue with Google
            </span>
          </button>

          <button onClick={() => handleSocialLogin('Apple')} className="w-full flex items-center justify-center gap-3 py-3 px-4 rounded-xl border border-gray-200 bg-gray-900 hover:bg-gray-800 active:scale-[0.98] transition-all" data-id="element-814">
            <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor" data-id="element-815">
              <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" data-id="element-816" />
            </svg>
            <span className="text-sm font-semibold text-white" data-id="element-817">
              Continue with Apple
            </span>
          </button>

          <button onClick={() => handleSocialLogin('Facebook')} className="w-full flex items-center justify-center gap-3 py-3 px-4 rounded-xl border border-gray-200 bg-[#1877F2] hover:bg-[#166FE5] active:scale-[0.98] transition-all" data-id="element-818">
            <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor" data-id="element-819">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" data-id="element-820" />
            </svg>
            <span className="text-sm font-semibold text-white" data-id="element-821">
              Continue with Facebook
            </span>
          </button>
        </div>

        <div className="text-center mt-6" data-id="element-822">
          <span className="text-gray-500 text-sm" data-id="element-823">Don't have an account? </span>
          <button onClick={() => navigate(Screen.SIGNUP)} className="text-primary font-bold text-sm hover:underline" data-id="element-824">
            Sign Up
          </button>
        </div>
      </div>
    </motion.div>;
}